import { z } from 'zod';

/**
 * O consumo de UMA chamada ao modelo feita por um subagente do Claude, sintetizado como evento
 * (R1.2 do plano de redução de tokens, 2026-11-04).
 *
 * O transcript da conversa principal não leva as chamadas do subagente: elas moram em
 * `<sessao>/subagents/agent-*.jsonl`, que o `Tail` não abre. Medido na semana de referência, isso deixava
 * a tela de Uso com 60-72% do gasto real. O daemon vê essas chamadas no stream do SDK (`assistant` com
 * `parent_tool_use_id`) e manda SÓ a contagem de tokens, no molde do `uso-do-motor` do Codex.
 *
 * **Não é conversa, e por isso não tem `message`.** A fala do subagente nunca vai ao banco; os extratores
 * de texto (`textoDoEvento`, a função SQL `remoto_texto_do_evento`, o `extracao.py` do cérebro) leem
 * `message.content` e devolvem vazio. O caso `claudeUsoDeSubagente` de `fixturas-texto.json` prende isso.
 * A chamada fica em `chamada`, e não em `message`, justamente para nenhum leitor a tomar por fala.
 *
 * `usage` segue a convenção do Claude (`input_tokens` NÃO inclui o cache) e a API a conta junto com os
 * eventos `assistant`, pela mesma identidade (`chamada.id`, o `message.id`).
 */

/** O `tipo` do `EventoSessao`. Nome novo no eixo `tipo`, que é `z.string()`: sem versão de protocolo. */
export const TIPO_USO_DE_SUBAGENTE = 'uso-de-subagente';

/** O sufixo do `uuidTurno`: `<message.id>:sa`. Um evento por `message.id` do subagente. */
export const SUFIXO_USO_DE_SUBAGENTE = ':sa';

const Contagem = z.number().int().nonnegative();

export const UsoDeSubagenteSchema = z.object({
  input_tokens: Contagem,
  output_tokens: Contagem,
  cache_read_input_tokens: Contagem,
  cache_creation_input_tokens: Contagem,
  /** O detalhe de TTL da escrita de cache. Ausente quando o CLI não o separou (tudo conta como 5 min). */
  cache_creation: z.object({ ephemeral_1h_input_tokens: Contagem }).optional(),
});
export type UsoDeSubagente = z.infer<typeof UsoDeSubagenteSchema>;

export const CargaUsoDeSubagenteSchema = z.object({
  type: z.literal(TIPO_USO_DE_SUBAGENTE),
  chamada: z.object({
    id: z.string().min(1),
    model: z.string().min(1).max(200).nullable(),
    usage: UsoDeSubagenteSchema,
  }),
});
export type CargaUsoDeSubagente = z.infer<typeof CargaUsoDeSubagenteSchema>;

export function uuidDoUsoDeSubagente(messageId: string): string {
  return `${messageId}${SUFIXO_USO_DE_SUBAGENTE}`;
}

function contagem(valor: unknown): number {
  return typeof valor === 'number' && Number.isFinite(valor) && valor >= 0 ? Math.floor(valor) : 0;
}

/**
 * Lê a mensagem `assistant` de subagente que o SDK entregou e monta a carga.
 *
 * **Nunca lança**: devolve `null` quando a mensagem não é de subagente (sem `parent_tool_use_id`), não tem
 * `message.id` (sem ele não há dedupe) ou o modelo é o `<synthetic>` do CLI (usage zerado). Copia só os
 * campos de contagem: nenhum `content` viaja.
 */
export function cargaDeUsoDeSubagente(mensagem: unknown): CargaUsoDeSubagente | null {
  if (typeof mensagem !== 'object' || mensagem === null) return null;
  const bruta = mensagem as Record<string, unknown>;
  if (bruta['type'] !== 'assistant') return null;
  const pai = bruta['parent_tool_use_id'];
  if (typeof pai !== 'string' || pai.length === 0) return null;
  const msg = bruta['message'];
  if (typeof msg !== 'object' || msg === null) return null;
  const m = msg as Record<string, unknown>;
  const id = m['id'];
  if (typeof id !== 'string' || id.length === 0) return null;
  const usage = m['usage'];
  if (typeof usage !== 'object' || usage === null) return null;
  const u = usage as Record<string, unknown>;
  const modelo = typeof m['model'] === 'string' && m['model'].length > 0 ? m['model'].slice(0, 200) : null;
  if (modelo === '<synthetic>') return null;

  const criacao = u['cache_creation'];
  const umaHora =
    typeof criacao === 'object' && criacao !== null
      ? (criacao as Record<string, unknown>)['ephemeral_1h_input_tokens']
      : undefined;
  return {
    type: TIPO_USO_DE_SUBAGENTE,
    chamada: {
      id,
      model: modelo,
      usage: {
        input_tokens: contagem(u['input_tokens']),
        output_tokens: contagem(u['output_tokens']),
        cache_read_input_tokens: contagem(u['cache_read_input_tokens']),
        cache_creation_input_tokens: contagem(u['cache_creation_input_tokens']),
        ...(typeof umaHora === 'number' ? { cache_creation: { ephemeral_1h_input_tokens: contagem(umaHora) } } : {}),
      },
    },
  };
}
